import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { slaSchema } from '../../utils/formSchema';
import { useFormik } from 'formik';
import { userRequest } from '../../utils/requestMethods';
import ButtonLoading from '../ButtonLoading';
import CategorySelector from '../common/CategorySelector';

const ServiceLevel = ({ schoolData, sla, onSlaAdded }) => {
    const [success, setSuccess] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [servicesLoading, setServicesLoading] = useState(false);
    const [selectedServices, setSelectedServices] = useState([]);
    
    const serviceCategories = [
        { value: 'school_management', label: 'School Management' },
        { value: 'financial_management', label: 'Financial Management' }
    ];

    // Initialize selectedServices from schoolData with display labels
    useEffect(() => {
        if (schoolData?.services) {
            const displayLabels = schoolData.services.map(value => {
                const category = serviceCategories.find(cat => cat.value === value);
                return category ? category.label : value;
            });
            setSelectedServices(displayLabels);
        }
    }, [schoolData]);

    useEffect(() => {
        if (sla && formik) {
            formik.setValues({
                ...sla,
            });
        }
    }, [sla]);
    
    const handleCategorySelect = (items) => {
        setSelectedServices(items);
    };

    const formik = useFormik({
        initialValues: {
            flexpay_fee: sla?.flexpay_fee || 0,
            paynow_fee: sla?.paynow_fee || 0,
            flexpay_markup: sla?.flexpay_markup || 0,
            sms_fee: sla?.sms_fee || 0,
        },
        validationSchema: slaSchema,
        onSubmit: (values) => {
            registerHandler(values);
        },
    });

    const registerHandler = async (values) => {
        setLoading(true);
        try {
            const allValues = {
                ...values,
            };
            await userRequest.post(`/schools/${schoolData._id}/settings`, {
                sla: {
                    ...allValues,
                },
            });

            setSuccess("Business service level agreement added");
            setTimeout(() => {
                setSuccess("");
                onSlaAdded();
            }, 2000);
        } catch (error) {
            console.log(error);
            setError(error?.response?.data || "Something went wrong, try again in a few minutes!");
            setTimeout(() => {
                setError("");
            }, 5000);
        } finally {
            setLoading(false);
        }
    };

    const servicesHandler = async () => {
        setServicesLoading(true);
        try {
            // Convert display labels back to API values before sending
            const apiValues = selectedServices.map(label => {
                const category = serviceCategories.find(cat => cat.label === label);
                return category ? category.value : label;
            });

            const response = await userRequest.put(`/schools/${schoolData?._id}`, {
                services: apiValues
            });
            if (response.data) {
                setSuccess("Services Added Successfully");
                onSlaAdded();
            }
        } catch (error) {
            console.log(error);
            setError(error?.response?.data?.message || "Something went wrong!");
            setTimeout(() => {
                setError("");
            }, 5000);
        } finally {
            setServicesLoading(false);
        }
    };
    
    return (
        <div className="bg-white border border-gray-200 rounded-3xl shadow">
            <div className="m-2 p-5 divide-y">
                <div className="py-2">
                    <h3 className="text-lg font-bold dark:text-gray-200">Service Level Setting</h3>
                    <p>Based on your business type, you will be required to submit the documents below during the business activation process.</p>
                    <hr />
                </div>
            </div>
            <div className="m-2 p-5 divide-y">
                {success &&
                    <div className="alert alert-success d-flex align-items-center" role="alert">
                        <i className="fal fa-check pr-10"></i>
                        <div>{success}</div>
                    </div>
                }
                {error && 
                    <div className="alert alert-warning d-flex align-items-center" role="alert">
                        <i className="fal fa-lock pr-10"></i>
                        <div>{error}</div>
                    </div>
                }
                <form onSubmit={formik.handleSubmit}>
                    <div className="grid grid-cols-1 gap-6">
                        <div className="lg:grid lg:grid-cols-2 lg:gap-x-2">
                            <div className="mb-2">
                                <p className="text-gray-700 dark:text-gray-200">Pay Now Transaction Fee</p>
                                <input type="number" id="paynow_fee" name="paynow_fee" placeholder="Legal Business Name" value={formik.values.paynow_fee} onBlur={formik.handleBlur} onChange={formik.handleChange} className="mt-1 block w-full rounded-md bg-gray-100 border-transparent focus:border-gray-500 focus:bg-white focus:ring-0"/>
                                {formik.errors.paynow_fee && formik.touched.paynow_fee &&
                                    <p className="mt-2 text-sm text-red-600 dark:text-red-500">{formik.errors.paynow_fee}</p>}
                            </div>
                            <div className="mb-2">
                                <p className="text-gray-700 dark:text-gray-200">Flexpay Transaction Fee</p>
                                <input type="number" id="flexpay_fee" name="flexpay_fee" placeholder="Legal Business Name" value={formik.values.flexpay_fee} onBlur={formik.handleBlur} onChange={formik.handleChange} className="mt-1 block w-full rounded-md bg-gray-100 border-transparent focus:border-gray-500 focus:bg-white focus:ring-0"/>
                                {formik.errors.flexpay_fee && formik.touched.flexpay_fee &&
                                    <p className="mt-2 text-sm text-red-600 dark:text-red-500">{formik.errors.flexpay_fee}</p>}
                            </div>
                        </div>
                        <div className="lg:grid lg:grid-cols-2 lg:gap-x-2">
                            <div className="mb-2">
                                <p className="text-gray-700 dark:text-gray-200">School Management Transaction Fee/Student</p>
                                <input type="number" id="sms_fee" name="sms_fee" placeholder="Legal Business Name" value={formik.values.sms_fee} onBlur={formik.handleBlur} onChange={formik.handleChange} className="mt-1 block w-full rounded-md bg-gray-100 border-transparent focus:border-gray-500 focus:bg-white focus:ring-0"/>
                                {formik.errors.sms_fee && formik.touched.sms_fee &&
                                    <p className="mt-2 text-sm text-red-600 dark:text-red-500">{formik.errors.sms_fee}</p>}
                            </div>
                            <div className="mb-2">
                                <p className="text-gray-700 dark:text-gray-200">School Flexpay Mark-Up</p>
                                <input type="number" id="flexpay_markup" name="flexpay_markup" placeholder="Legal Business Name" value={formik.values.flexpay_markup} onBlur={formik.handleBlur} onChange={formik.handleChange} className="mt-1 block w-full rounded-md bg-gray-100 border-transparent focus:border-gray-500 focus:bg-white focus:ring-0"/>
                                {formik.errors.flexpay_markup && formik.touched.flexpay_markup &&
                                    <p className="mt-2 text-sm text-red-600 dark:text-red-500">{formik.errors.flexpay_markup}</p>}
                            </div>
                        </div>
                        <div className="px-4 py-3 text-right sm:px-6">
                            {loading ? (
                                <ButtonLoading styles="inline-flex justify-center py-2 px-8 border border-transparent shadow-sm text-md font-medium rounded-full text-white bg-indigo-800" />
                            ) : (
                                <button type="submit" className="inline-flex justify-center py-2 px-8 border border-transparent shadow-sm text-md font-medium rounded-full text-white bg-indigo-800 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">Save SLA Details</button>
                            )}
                        </div>
                    </div>
                </form>

                <div className="mt-6">
                    <h4 className="text-lg font-semibold mb-4">Select Services</h4>
                    <CategorySelector
                        label="Select Services"
                        categories={serviceCategories.map(cat => cat.label)}
                        initialSelected={selectedServices}
                        onSelectionChange={handleCategorySelect}
                    />
                    <div className="px-4 py-3 text-right sm:px-6">
                        {servicesLoading ? (
                            <ButtonLoading styles="inline-flex justify-center py-2 px-8 border border-transparent shadow-sm text-md font-medium rounded-full text-white bg-indigo-800" />
                        ) : (
                            <button 
                                type="button" 
                                onClick={servicesHandler}
                                className="inline-flex justify-center py-2 px-8 border border-transparent shadow-sm text-md font-medium rounded-full text-white bg-indigo-800 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                                Save Services
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ServiceLevel
